import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue, useResetRecoilState } from 'recoil';
import { cartState } from '../../../atoms/cartAtom';
import { createOrder } from '../../admin/orders-controller/order-controller';
import { CartService } from '../../../services/cartService';
import { fetchSettings } from '../../admin/settings/controller/settings-controller';
import { promoCodeValidationService } from '../../../services/promoCodeValidationService';

// Components
import Navbar from '../../../components/Navbar';
import Footer from '../../../components/Footer';
import { 
  HiOutlineShoppingBag, 
  HiOutlineCheck, 
  HiOutlineX, 
  HiOutlineUser, 
  HiOutlineMail, 
  HiOutlinePhone, 
  HiOutlineLocationMarker, 
  HiOutlineOfficeBuilding, 
  HiOutlineMailOpen 
} from 'react-icons/hi';

// Tunisian cities with codes
const tunisianCities = [
  { Code: "TUN", EnName: "Tunis" },
  { Code: "SFX", EnName: "Sfax" },
  { Code: "SOS", EnName: "Sousse" },
  { Code: "KRN", EnName: "Kairouan" },
  { Code: "BIZ", EnName: "Bizerte" },
  { Code: "GBS", EnName: "Gabes" },
  { Code: "AM", EnName: "Ariana" },
  { Code: "GFS", EnName: "Gafsa" },
  { Code: "MNS", EnName: "Monastir" },
  { Code: "BA", EnName: "Ben Arous" },
  { Code: "KSR", EnName: "Kasserine" },
  { Code: "MDN", EnName: "Medenine" },
  { Code: "NAB", EnName: "Nabeul" },
  { Code: "TAN", EnName: "Tataouine" },
  { Code: "BJA", EnName: "Beja" },
  { Code: "JDB", EnName: "Jendouba" },
  { Code: "KEF", EnName: "Kef" },
  { Code: "MHD", EnName: "Mahdia" },
  { Code: "SBZ", EnName: "Sidi Bouzid" },
  { Code: "TZR", EnName: "Tozeur" },
  { Code: "SLI", EnName: "Siliana" },
  { Code: "KBL", EnName: "Kebili" },
  { Code: "ZAG", EnName: "Zaghouan" },
  { Code: "MAN", EnName: "Manouba" },
  { Code: "Libya", EnName: "Libya" }
];

const CheckoutPage = () => {
  const cart = useRecoilValue(cartState);
  const resetCart = useResetRecoilState(cartState);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: 'TUN',
    codePromo: '',
    notes: '',
    paymentMethod: 'cash',
    terms: true
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [deliveryFee, setDeliveryFee] = useState(7); // Default value until fetched from settings
  const phoneInputRef = useRef(null);
  const [promoState, setPromoState] = useState({ isValid: false, discount: 0, validating: false, error: null });
  
  // Fetch settings including delivery fee on component mount
  useEffect(() => {
    const getSettings = async () => {
      try {
        const settings = await fetchSettings();
        if (settings && settings.fraisDeLivraison !== undefined) {
          setDeliveryFee(settings.fraisDeLivraison);
          console.log('Delivery fee loaded from settings:', settings.fraisDeLivraison);
        }
      } catch (error) {
        console.error('Error fetching delivery fee:', error);
      }
    };

    getSettings();
  }, []);
  
  useEffect(() => {
    // Only redirect if cart is empty AND we're not processing an order
    const isProcessingOrder = localStorage.getItem('processingOrder') === 'true';
    
    if ((!cart.items || cart.items.length === 0) && !isProcessingOrder) {
      navigate('/catalog');
    }
    
    // Set page title
    document.title = 'Finaliser votre commande';
    
    // Scroll to top
    window.scrollTo(0, 0);
  }, [cart.items, navigate]);
  
  // Calculate if eligible for free delivery
  // New logic: free delivery if any product has quantity > 1 OR if there's more than one product
  const isFreeDelivery = cart.items && (
    cart.items.length > 1 || // More than one different product
    cart.items.some(item => item.quantity > 1) // At least one product with quantity > 1
  );
  
  // Calculate order totals
  const cartSubtotal = cart.total || 0;
  const finalDeliveryFee = isFreeDelivery ? 0 : deliveryFee;
  const promoDiscount = promoState.isValid ? Math.min(promoState.discount || 0, cartSubtotal) : 0;
  const discountedSubtotal = Math.max(0, cartSubtotal - promoDiscount);
  const orderTotal = discountedSubtotal + finalDeliveryFee;

  // Validate the promo code on blur
  const handlePromoBlur = async () => {
    const code = (formData.codePromo || '').trim();
    if (!code) {
      setPromoState({ isValid: false, discount: 0, validating: false, error: null });
      return;
    }
    setPromoState(prev => ({ ...prev, validating: true, error: null }));
    try {
      const result = await promoCodeValidationService.checkPromoCode(code);
      if (result.success) {
        const montant = Number(result.data?.montant || 0);
        setPromoState({ isValid: montant > 0, discount: montant, validating: false, error: montant > 0 ? null : 'Code promo invalide' });
      } else {
        setPromoState({ isValid: false, discount: 0, validating: false, error: result.error || 'Code promo invalide' });
      }
    } catch (err) {
      setPromoState({ isValid: false, discount: 0, validating: false, error: 'Erreur de validation' });
    }
  };
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Reset promo validation when the code is edited
    if (name === 'codePromo') {
      setPromoState({ isValid: false, discount: 0, validating: false, error: null });
    }

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.firstName) newErrors.firstName = 'Prénom requis';
    if (!formData.lastName) newErrors.lastName = 'Nom requis';
    if (!formData.phone) newErrors.phone = 'Téléphone requis';
    if (!formData.address) newErrors.address = 'Adresse requise';
    if (!formData.city) newErrors.city = 'Ville requise';
    if (!formData.terms) newErrors.terms = 'Vous devez accepter les conditions';
    
    // Phone validation (simple Tunisian phone number check)
    if (formData.phone && !/^[2-9]\d{7}$/.test(formData.phone)) {
      newErrors.phone = 'Numéro de téléphone invalide (doit contenir 8 chiffres et commencer par 2-9)';
    }
    
    // Email validation if provided
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
  e.preventDefault();
  
  if (!validateForm()) {
    return;
  }
  
  setIsSubmitting(true);
  
  try {
    // Compute per-line proportional promo discount so totalRemise is saved
    const lineTTCs = cart.items.map(item => Number(item.price) * Number(item.quantity));
    const totalLinesTTC = lineTTCs.reduce((s, v) => s + v, 0);

    // Prepare order data to match the admin implementation
    const orderData = {
      entet: {
        dateBonCommande: new Date().toISOString(),
        reference: `CMD-${Date.now()}`,
        utilisateurId: null,
        totalTTC: Number(orderTotal.toFixed(2)),
        totalHt: Number((discountedSubtotal / 1.19).toFixed(2)),
        totalTva: Number((discountedSubtotal - (discountedSubtotal / 1.19)).toFixed(2)),
        totalRemise: Number(promoDiscount.toFixed(2)),
        codePromo: promoState.isValid ? (formData.codePromo || '') : '',
        source: "web_store",
        etat: "pending",
        fraisDeLivraison: Number(finalDeliveryFee.toFixed(2)),
        plateformId: null,
        clientFirstName: formData.firstName,
        clientLastName: formData.lastName,
        clientNumber: formData.phone,
        clientAdresse: formData.address,
        clientEmail: formData.email || "",
        ville: formData.city,
        notes: formData.notes || ""
      },
      line: cart.items.map((item, idx) => {
        const unitPrice = Number(item.price);
        const qty = Number(item.quantity);
        const lineTTCBeforePromo = unitPrice * qty;
        // Proportional promo share for this line
        const promoShare = totalLinesTTC > 0 ? (lineTTCBeforePromo / totalLinesTTC) * promoDiscount : 0;
        const lineTTCAfterPromo = Math.max(0, lineTTCBeforePromo - promoShare);
        const lineHT = lineTTCAfterPromo / 1.19;
        const lineTva = lineTTCAfterPromo - lineHT;

        return {
          productVariantId: item.variantId || null,
          productId: item.productId,
          quantite: qty,
          prixUnitaire: Number(unitPrice.toFixed(2)),
          totalTTC: Number(lineTTCAfterPromo.toFixed(2)),
          totalHt: Number(lineHT.toFixed(2)),
          totalTva: Number(lineTva.toFixed(2)),
          tauxTva: 19,
          tauxRemise: 0,
          totalRemise: Number(promoShare.toFixed(2))
        };
      })
    };
    
    console.log('Sending order data:', orderData);
    
    // Call the same API endpoint as admin
    const result = await createOrder(orderData);
    
    // Store the order ID for redirect after the modal
    const orderId = result.id;
    
    // Show success message
    setShowSuccess(true);
    
    // Save a flag in localStorage to prevent the automatic redirect
    localStorage.setItem('processingOrder', 'true');
    
    // Clear cart but do NOT navigate yet
    resetCart();
    CartService.clearCart();
    
    // Wait a bit before redirecting to order confirmation
    setTimeout(() => {
      // Remove the processing flag
      localStorage.removeItem('processingOrder');
      // Redirect to order confirmation
      navigate(`/order-confirmation/${orderId}`);
    }, 2000);
  } catch (error) {
    console.error('Error creating order:', error);
    setErrors(prev => ({ 
      ...prev, 
      form: 'Une erreur est survenue. Veuillez réessayer.' 
    }));
  } finally {
    setIsSubmitting(false);
  }
};
  
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-grow pt-32 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl font-bold mb-8 text-gray-900">Finaliser votre commande</h1>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Checkout form */}
              <div className="lg:col-span-2">
                <div className="bg-white shadow rounded-lg p-6 border border-gray-200">
                  <form onSubmit={handleSubmit}>
                    <div className="space-y-6">
                      {/* Personal information section */}
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Informations personnelles</h3>
                        <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6">
                          {/* First Name */}
                          <div className="sm:col-span-3">
                            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                              Prénom (الاسم) <span className="text-red-500">*</span>
                            </label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <HiOutlineUser className="h-5 w-5 text-gray-400" />
                              </div>
                              <input
                                type="text"
                                id="firstName"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                className={`pl-10 block w-full py-3 border ${errors.firstName
                                    ? 'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
                                    : 'border-gray-300 focus:ring-[#174540] focus:border-[#174540]'
                                  } rounded-md shadow-sm placeholder-gray-400 sm:text-sm transition-all`}
                                placeholder="Votre prénom"
                              />
                            </div>
                            {errors.firstName && (
                              <p className="mt-2 text-sm text-red-600">{errors.firstName}</p>
                            )}
                          </div>

                          {/* Last Name */}
                          <div className="sm:col-span-3">
                            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                              Nom (اللقب) <span className="text-red-500">*</span>
                            </label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <HiOutlineUser className="h-5 w-5 text-gray-400" />
                              </div>
                              <input
                                type="text"
                                id="lastName"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                className={`pl-10 block w-full py-3 border ${errors.lastName
                                    ? 'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
                                    : 'border-gray-300 focus:ring-[#174540] focus:border-[#174540]'
                                  } rounded-md shadow-sm placeholder-gray-400 sm:text-sm transition-all`}
                                placeholder="Votre nom"
                              />
                            </div>
                            {errors.lastName && (
                              <p className="mt-2 text-sm text-red-600">{errors.lastName}</p>
                            )}
                          </div>

                          {/* Email */}
                          <div className="sm:col-span-3">
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                              Email (البريد الإلكتروني)
                            </label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <HiOutlineMail className="h-5 w-5 text-gray-400" />
                              </div>
                              <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                className={`pl-10 block w-full py-3 border ${errors.email
                                    ? 'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
                                    : 'border-gray-300 focus:ring-[#174540] focus:border-[#174540]'
                                  } rounded-md shadow-sm placeholder-gray-400 sm:text-sm transition-all`}
                                placeholder="exemple@email.com"
                              />
                            </div>
                            {errors.email && (
                              <p className="mt-2 text-sm text-red-600">{errors.email}</p>
                            )}
                          </div>

                          {/* Phone */}
                          <div className="sm:col-span-3">
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                              Téléphone (رقم الهاتف) <span className="text-red-500">*</span>
                            </label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <HiOutlinePhone className="h-5 w-5 text-gray-400" />
                              </div>
                              <input
                                type="tel"
                                id="phone"
                                name="phone"
                                ref={phoneInputRef}
                                value={formData.phone}
                                onChange={handleChange}
                                maxLength={8}
                                className={`pl-10 block w-full py-3 border ${errors.phone
                                    ? 'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
                                    : 'border-gray-300 focus:ring-[#174540] focus:border-[#174540]'
                                  } rounded-md shadow-sm placeholder-gray-400 sm:text-sm transition-all`}
                                placeholder="Votre numéro (8 chiffres)"
                              />
                            </div>
                            {errors.phone && (
                              <p className="mt-2 text-sm text-red-600">{errors.phone}</p>
                            )}
                          </div>

                          {/* Address */}
                          <div className="sm:col-span-6">
                            <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                              Adresse (العنوان) <span className="text-red-500">*</span>
                            </label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <HiOutlineLocationMarker className="h-5 w-5 text-gray-400" />
                              </div>
                              <input
                                type="text"
                                id="address"
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                className={`pl-10 block w-full py-3 border ${errors.address
                                    ? 'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
                                    : 'border-gray-300 focus:ring-[#174540] focus:border-[#174540]'
                                  } rounded-md shadow-sm placeholder-gray-400 sm:text-sm transition-all`}
                                placeholder="Adresse complète"
                              />
                            </div>
                            {errors.address && (
                              <p className="mt-2 text-sm text-red-600">{errors.address}</p>
                            )}
                          </div>

                          {/* City */}
                          <div className="sm:col-span-3">
                            <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                              Ville (المدينة) <span className="text-red-500">*</span>
                            </label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <HiOutlineOfficeBuilding className="h-5 w-5 text-gray-400" />
                              </div>
                              <select
                                name="city"
                                id="city"
                                className={`pl-10 block w-full py-3 border ${errors.city
                                    ? 'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
                                    : 'border-gray-300 focus:ring-[#174540] focus:border-[#174540]'
                                  } rounded-md shadow-sm text-gray-900 sm:text-sm transition-all`}
                                value={formData.city}
                                onChange={handleChange}
                              >
                                {tunisianCities.map((city) => (
                                  <option key={city.Code} value={city.Code}>
                                    {city.EnName}
                                  </option>
                                ))}
                              </select>
                            </div>
                            {errors.city && (
                              <p className="mt-2 text-sm text-red-600">{errors.city}</p>
                            )}
                          </div>

                          {/* Promo Code */}
                          <div className="sm:col-span-3">
                            <label htmlFor="codePromo" className="block text-sm font-medium text-gray-700">
                              Code promo (رمز التخفيض)
                            </label>
                            <div className="mt-1 relative rounded-md shadow-sm">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <HiOutlineMailOpen className="h-5 w-5 text-gray-400" />
                              </div>
                              <input
                                type="text"
                                name="codePromo"
                                id="codePromo"
                                value={formData.codePromo}
                                onChange={handleChange}
                                onBlur={handlePromoBlur}
                                className="pl-10 block w-full py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:ring-[#174540] focus:border-[#174540] sm:text-sm transition-all"
                                placeholder="Code promo (optionnel)"
                              />
                            </div>
                            {promoState.validating && (
                              <p className="mt-1 text-xs text-gray-500">Vérification...</p>
                            )}
                            {!promoState.validating && promoState.isValid && (
                              <p className="mt-1 text-xs text-green-600">Code valide: -{Number(promoState.discount).toFixed(2)} TND</p>
                            )}
                            {!promoState.validating && promoState.error && (
                              <p className="mt-1 text-xs text-red-600">{promoState.error}</p>
                            )}
                          </div>
                          
                          {/* Notes */}
                          <div className="sm:col-span-6">
                            <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                              Notes pour la livraison
                            </label>
                            <textarea
                              id="notes"
                              name="notes"
                              rows="3"
                              value={formData.notes}
                              onChange={handleChange}
                              className="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:ring-[#174540] focus:border-[#174540] sm:text-sm"
                              placeholder="Instructions spéciales pour la livraison..."
                            ></textarea>
                          </div>
                        </div>
                      </div>
                      
                      {/* Delivery information */}
                      <div className="p-4 bg-green-50 border border-green-100 rounded-lg">
                        <div className="flex">
                          <svg className="h-5 w-5 text-green-500 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <div className="ml-3">
                            <p className="text-sm text-green-700">
                              {isFreeDelivery
                                ? <span className="font-medium">Livraison gratuite! Vous bénéficiez de la livraison gratuite.</span>
                                : <span>Livraison <span className="font-medium">{deliveryFee.toFixed(2)} TND</span>. Gratuite dès <span className="font-medium">2 articles</span> ou si vous commandez plus d'une pièce d'un même produit.</span>
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Payment method */}
                      <div className="pt-4 border-t border-gray-200">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Méthode de paiement</h3>
                        <div className="space-y-3">
                          <div className="flex items-center">
                            <input
                              id="cash"
                              name="paymentMethod"
                              type="radio"
                              value="cash"
                              checked={formData.paymentMethod === 'cash'}
                              onChange={handleChange}
                              className="h-4 w-4 text-[#174540] focus:ring-[#174540] border-gray-300"
                            />
                            <label htmlFor="cash" className="ml-3 block text-sm font-medium text-gray-700">
                              Paiement à la livraison
                            </label>
                          </div>
                        </div>
                      </div>
                      
                      {/* Terms and conditions */}
                      <div className="pt-4 border-t border-gray-200">
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <input
                              id="terms"
                              name="terms"
                              type="checkbox"
                              checked={formData.terms}
                              onChange={handleChange}
                              className="h-4 w-4 text-[#174540] focus:ring-[#174540] border-gray-300 rounded"
                            />
                          </div>
                          <div className="ml-3">
                            <label htmlFor="terms" className="text-sm font-medium text-gray-700">
                              J'accepte les conditions générales de vente <span className="text-red-500">*</span>
                            </label>
                            {errors.terms && <p className="mt-1 text-sm text-red-600">{errors.terms}</p>}
                          </div>
                        </div>
                      </div>
                      
                      {/* Error message */}
                      {errors.form && (
                        <div className="rounded-md bg-red-50 p-4">
                          <div className="flex">
                            <div className="flex-shrink-0">
                              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293-1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="ml-3">
                              <p className="text-sm font-medium text-red-800">{errors.form}</p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Submit button */}
                      <div>
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full inline-flex justify-center items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-[#174540] hover:bg-[#0f322e] focus:outline-none transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                          {isSubmitting ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Traitement...
                            </>
                          ) : (
                            'Confirmer la commande'
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
              
              {/* Order summary */}
              <div>
                <div className="bg-white shadow rounded-lg p-6 border border-gray-200 sticky top-32">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Récapitulatif de commande</h3>
                  <div className="space-y-4 max-h-80 overflow-y-auto mb-4 pr-2">
                    {cart.items && cart.items.map(item => (
                      <div key={item.cartItemId} className="flex items-start">
                        <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-md border border-gray-200">
                          <img
                            src={item.image || 'https://via.placeholder.com/150'}
                            alt={item.name}
                            className="h-full w-full object-cover object-center"
                          />
                        </div>
                        <div className="ml-4 flex-1">
                          <h4 className="text-sm font-medium text-gray-900">{item.name || 'Produit'}</h4>
                          {item.variantName && (
                            <p className="text-xs text-gray-500">{item.variantName}</p>
                          )}
                          <div className="flex justify-between mt-1">
                            <p className="text-xs text-gray-500">{item.quantity} x {parseFloat(item.price).toFixed(2)} TND</p>
                            <p className="text-sm font-medium text-gray-900">{(parseFloat(item.price) * item.quantity).toFixed(2)} TND</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="border-t border-gray-200 pt-4 space-y-2">
                    <div className="flex justify-between">
                      <p className="text-sm text-gray-600">Sous-total</p>
                      <p className="text-sm font-medium text-gray-900">{cartSubtotal.toFixed(2)} TND</p>
                    </div>
                    {promoDiscount > 0 && (
                      <div className="flex justify-between">
                        <p className="text-sm text-green-600">Remise code promo</p>
                        <p className="text-sm font-medium text-green-600">-{promoDiscount.toFixed(2)} TND</p>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <p className="text-sm text-gray-600">Livraison</p>
                      <p className="text-sm font-medium text-gray-900">
                        {isFreeDelivery ? 'Gratuite' : `${deliveryFee.toFixed(2)} TND`}
                      </p>
                    </div>
                    <div className="flex justify-between border-t border-gray-200 pt-2">
                      <p className="text-base font-medium text-gray-900">Total</p>
                      <p className="text-base font-bold text-gray-900">{orderTotal.toFixed(2)} TND</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      {/* Success modal */}
      {showSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-center mb-4">
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <HiOutlineCheck className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <h3 className="text-lg font-medium text-center text-gray-900 mb-2">Commande confirmée !</h3>
            <p className="text-center text-gray-600 mb-6">
              Merci pour votre commande. Vous allez être redirigé vers la page de confirmation...
            </p>
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default CheckoutPage;